import { useQuery } from "@tanstack/react-query";
import { ScrollText } from "lucide-react";
import { useMemo, useState } from "react";
import { JsonBlock } from "../../components/json-block";
import { Pagination } from "../../components/pagination";
import type { CursorQuery } from "../../lib/api";
import { adminApi } from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";

const PAGE_SIZE = 50;

export function AuditTab({ accountId }: { accountId: string }) {
  const { token } = useAdminAuth();
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const currentCursor = cursorStack[pageIndex] ?? null;
  const query = useMemo<CursorQuery>(
    () => ({
      ...(currentCursor ? { cursor: currentCursor } : {}),
      take: PAGE_SIZE,
    }),
    [currentCursor],
  );

  const audit = useQuery({
    queryKey: ["admin", "account", accountId, "audit", query],
    queryFn: () => adminApi.audit(token!, accountId, query),
    enabled: Boolean(token),
    staleTime: 0,
    refetchOnMount: "always",
  });

  function handleForward() {
    const nextCursor = audit.data?.nextCursor;
    if (!nextCursor) return;
    setCursorStack((current) => [
      ...current.slice(0, pageIndex + 1),
      nextCursor,
    ]);
    setPageIndex((current) => current + 1);
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Журнал аудита</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          История административных действий в этом аккаунте.
        </p>
      </div>

      {audit.isLoading ? (
        <div className="panel p-4 text-sm text-muted-foreground">
          Загружаем аудит...
        </div>
      ) : audit.error ? (
        <div className="panel p-4 text-sm text-danger">
          {audit.error.message}
        </div>
      ) : audit.data?.items.length === 0 ? (
        <div className="panel flex items-center gap-3 p-4 text-sm text-muted-foreground">
          <ScrollText className="h-5 w-5" />
          Записей аудита пока нет.
        </div>
      ) : (
        <div className="space-y-3">
          {(audit.data?.items ?? []).map((entry) => (
            <article key={entry.id} className="panel overflow-hidden">
              <div className="grid gap-3 border-b p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-xs text-muted-foreground">Действие</p>
                  <p className="break-words font-semibold">{entry.action}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Администратор</p>
                  <p className="break-words font-medium">
                    {entry.adminUsername}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Цель</p>
                  <p className="break-all font-medium">
                    {entry.targetType} · {entry.targetId}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Время</p>
                  <time className="font-medium" dateTime={entry.createdAt}>
                    {formatDateTime(entry.createdAt)}
                  </time>
                </div>
              </div>
              <div className="grid gap-4 p-4 lg:grid-cols-3">
                <section>
                  <h3 className="mb-2 text-sm font-medium">До</h3>
                  <JsonBlock value={entry.before} />
                </section>
                <section>
                  <h3 className="mb-2 text-sm font-medium">После</h3>
                  <JsonBlock value={entry.after} />
                </section>
                <section>
                  <h3 className="mb-2 text-sm font-medium">Метаданные</h3>
                  <JsonBlock value={entry.metadata} />
                </section>
              </div>
            </article>
          ))}
        </div>
      )}

      <Pagination
        canGoBack={pageIndex > 0}
        canGoForward={Boolean(audit.data?.nextCursor)}
        onBack={() => setPageIndex((current) => Math.max(0, current - 1))}
        onForward={handleForward}
        isLoading={audit.isFetching}
      />
    </div>
  );
}
